import * as Sentry from '@sentry/react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createRootRouteWithContext, Outlet } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { useTranslation } from 'react-i18next';

import { ConsentBanner } from '@/components/consent/consent-banner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useDocumentTitle } from '@/hooks/primitives/use-document-title';
import { env } from '@/lib/env';
import type { RouterContext } from '@/types/router';
import { loadNamespaces } from '@/lib/i18n/i18n';
import { PAGE_TITLE } from '@/components/ui/typography';

const RootLayout = () => {
  useDocumentTitle();
  return (
    <Sentry.ErrorBoundary fallback={<RootErrorFallback />}>
      <TooltipProvider>
        <Outlet />
      </TooltipProvider>
      <ConsentBanner />
      {import.meta.env.DEV && env.VITE_HIDE_TQR_DEVTOOLS !== 'true' && (
        <>
          <ReactQueryDevtools buttonPosition="bottom-left" />
          <TanStackRouterDevtools position="bottom-right" />
        </>
      )}
    </Sentry.ErrorBoundary>
  );
};

const RootErrorFallback = () => {
  const { t } = useTranslation();
  return (
    <div className="bg-background flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h1 className={PAGE_TITLE}>{t('root_error.title')}</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          {t('root_error.hint')}
        </p>
      </div>
    </div>
  );
};

export const Route = createRootRouteWithContext<RouterContext>()({
  loader: () => loadNamespaces('billing'),
  component: RootLayout,
});
